import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { mastraClient } from '@/lib/mastra-client';
import { fetchAgingReceivable } from '../api';
import AgingPage from './AgingPage';
import { AgentPanel } from '@/shared/components/AgentPanel';
import { ComparisonSheetContainer } from '@/features/qbo-invoices';
import type { TriageRow } from '../types/triage';

interface ProcessResult {
  step0_qbSync?: { pgInvoicesChecked: number; missingFromQbo: string[] };
  step1_bankIngest?: { newlyIngested?: number | null; matched?: number | null; ambiguous?: number | null };
  step2_customerReplies?: { scanned: number; matchedToInvoice: number };
  step3_triage?: TriageRow[];
  step4_actions?: Record<string, string[]>;
}

// localStorage key — persist last triage across reloads so the badges stay
// visible after refresh without re-running the pipeline.
const TRIAGE_STORAGE_KEY = 'atek-ar-process-triage';

export default function AccountsReceivablePage() {
  const queryClient = useQueryClient();
  // Open the full 3-way comparison sheet (Mongo / V4 / QBO) inline — same
  // component as `/acc/invoices`, but URL stays on `/acc/ar` so the user
  // doesn't lose their aging context. ComparisonSheetContainer fetches all
  // the underlying invoice data internally given just the doc number.
  const [openInvoiceNumber, setOpenInvoiceNumber] = useState<string | null>(null);
  const [pipelineRunning, setPipelineRunning] = useState(false);

  // Persist + restore latest triage so badges survive reloads.
  const [triageRows, setTriageRows] = useState<TriageRow[]>(() => {
    try {
      const raw = localStorage.getItem(TRIAGE_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as TriageRow[]) : [];
    } catch { return []; }
  });
  const triageByInvoice = useMemo(() => {
    const m = new Map<string, TriageRow>();
    for (const t of triageRows) m.set(t.invoiceNumber, t);
    return m;
  }, [triageRows]);

  async function handleRunPipeline() {
    setPipelineRunning(true);
    const toastId = toast.loading('Running acc-ar-process… (bank ingest + customer replies + per-invoice triage)');
    try {
      const run = await mastraClient.getWorkflow('acc-ar-process').createRun();
      const data = await run.startAsync({
        inputData: { limit: 200, bankLookbackHours: 168, amountTolerance: 0.02 },
      });
      const result = ((data as { result?: ProcessResult }).result ?? (data as ProcessResult)) || {};

      // Persist the per-invoice triage so InvoiceRows can render badges.
      if (result.step3_triage && result.step3_triage.length > 0) {
        setTriageRows(result.step3_triage);
        try { localStorage.setItem(TRIAGE_STORAGE_KEY, JSON.stringify(result.step3_triage)); } catch { /* ignore */ }
      }

      // Refresh the aging table from QuickBooks now that bank deposits may
      // have been applied during the pipeline.
      await queryClient.invalidateQueries({ queryKey: ['aging-receivable'] });

      const actions = result.step4_actions ?? {};
      const lines = [
        actions.apply_payment?.length ? `apply_payment: ${actions.apply_payment.length}` : '',
        actions.await_deposit?.length ? `await_deposit: ${actions.await_deposit.length}` : '',
        actions.escalate_ap?.length ? `escalate_ap: ${actions.escalate_ap.length}` : '',
        actions.dunner?.length ? `dunner: ${actions.dunner.length}` : '',
        actions.qb_sync_needed?.length ? `qb_sync_needed: ${actions.qb_sync_needed.length}` : '',
      ].filter(Boolean).join(' · ');

      toast.success('AR pipeline complete', {
        id: toastId,
        description: lines || 'No actionable invoices.',
      });
    } catch (err: any) {
      toast.error('AR pipeline failed', { id: toastId, description: err?.message ?? String(err) });
    } finally {
      setPipelineRunning(false);
    }
  }

  return (
    <>
      <AgingPage
        title="Accounts Receivable"
        description="Aging summary of outstanding customer invoices from QuickBooks."
        queryKey="aging-receivable"
        fetchFn={fetchAgingReceivable}
        drilldown={{
          enabled: true,
          onInvoiceClick: (inv) => setOpenInvoiceNumber(inv.docNumber),
        }}
        triageByInvoice={triageByInvoice}
        csvFilePrefix="ar"
        extraHeaderActions={
          <Button
            variant="default"
            size="sm"
            onClick={handleRunPipeline}
            disabled={pipelineRunning}
            title="Run the full AR pipeline: bank ingest → customer replies → per-invoice triage → action queue"
          >
            <Play className={cn('h-4 w-4', pipelineRunning && 'animate-pulse')} />
            <span className="ml-1">{pipelineRunning ? 'Running…' : 'Run pipeline'}</span>
          </Button>
        }
      />

      <ComparisonSheetContainer
        open={!!openInvoiceNumber}
        onOpenChange={(open) => { if (!open) setOpenInvoiceNumber(null); }}
        invoiceNumber={openInvoiceNumber}
      />
      {/* BookKeeper agent panel — page-scoped, green, right-side drawer. */}
      <AgentPanel
        agentId="bookkeeper"
        name="BookKeeper"
        tagline="AR assistant"
        intro="Reconcile invoices, hunt payment evidence, draft QB corrections. Ask me about any open invoice."
        placeholder="Ask BookKeeper about an invoice..."
        variant="green"
        storagePrefix="atek-ar-"
        defaultPosition={{ right: 96, bottom: 24 }}
        panelMode="side"
      />
    </>
  );
}
